import styled from '@emotion/styled'
import { useSlides } from 'features/slides'
import { FC, useCallback, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useSessionMode } from 'session-mode/use-session-mode'
import { useStageSurface } from 'room/common/stage-surface'
import { useMyProfile } from 'users'

/**
 * `centred` only in person. A slide sizes itself to its own aspect ratio, so
 * on a 1920px projector it leaves a few hundred pixels over - and without this
 * they all land on the right, putting the slide hard against the left edge.
 * Online keeps the original left alignment: the stage there sits beside the
 * video columns, and changing it would move a screen this branch has no
 * business moving.
 */
const Container = styled.div<{ centred: boolean }>`
	display: flex;
	width: 100%;
	height: 100%;
	min-height: 0;
	position: relative;
	max-width: 100%;

	min-width: 100%;
	width: 100%;
	flex: 1;
	min-width: 0;
	${({ centred }) => (centred ? 'justify-content: center;' : '')}

	/* The slide fits whatever width it is given - beside the presenter panel
	   that is less than the stage - so its element must be able to shrink. */
	& > beelday-slide-renderer {
		display: block;
		position: relative;
		width: 100%;
		height: 100%;
		min-width: 0;
		overflow: hidden;
	}
`

export const SlideRenderer: FC<{ slideId: string }> = ({ slideId }) => {
	const location = useLocation<{ from?: string }>()
	const history = useHistory()
	const ref = useRef<HTMLDivElement>(null)
	const { closeSlide } = useSlides()

	const close = useCallback(() => {
		closeSlide()
	}, [closeSlide])

	useEffect(() => {
		const element = ref.current
		element?.addEventListener('close', close)

		return () => element?.removeEventListener('close', close)
	}, [history, location, close])

	const profile = useMyProfile()
	// The projector of presenter mode shows the slide alone: no close, no
	// annotation tools - the trainer has both on the console.
	const onProjector = useStageSurface() === 'projector'

	return (
		<Container centred={useSessionMode() === 'IN_PERSON'}>
			<beelday-slide-renderer
				slideid={slideId}
				withclose={profile.isTrainer && !onProjector}
				viewonly={onProjector}
				ref={ref}
			/>
		</Container>
	)
}
